import {
  getGetClinicQueryKey,
  getListClinicActivityQueryKey,
  getListClinicsQueryKey,
  useDeactivateClinic,
  useReactivateClinic,
  type ClinicDetail,
} from '@pa/api/admin'
import { ConfirmDialog, toastApiError } from '@pa/staff'
import { useToast } from '@pa/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Power, PowerOff } from 'lucide-react'
import { useState } from 'react'

/**
 * The S-04 header's confirmation copy (UserFlows S-04 "Deactivate / Reactivate (with confirmation)"). UserFlows gives
 * no text: the dialogs state the two effects (task 13 assumption). No reason is collected (#42's `reason` is optional).
 */
export const statusCopy = (name: string) => ({
  deactivate: {
    title: `Deactivate ${name}?`,
    body: "Online booking stops: patients see “Online booking isn't available right now”. The clinic's staff can't sign in until you reactivate it.",
    action: 'Deactivate',
    done: 'Clinic deactivated',
  },
  reactivate: {
    title: `Reactivate ${name}?`,
    body: "Online booking opens again for patients, and the clinic's staff can sign in again.",
    action: 'Reactivate',
    done: 'Clinic reactivated',
  },
})

/** S-04 header: **Deactivate** (active clinic) or **Reactivate** (inactive), each with its confirmation (#42, #43). */
export function StatusAction({ clinic }: { clinic: ClinicDetail }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const deactivate = useDeactivateClinic()
  const reactivate = useReactivateClinic()
  const [open, setOpen] = useState(false)
  const kind = clinic.status === 'inactive' ? 'reactivate' : 'deactivate'
  const copy = statusCopy(clinic.name)[kind]

  async function confirm() {
    try {
      const updated =
        kind === 'deactivate'
          ? await deactivate.mutateAsync({ clinicId: clinic.id, data: {} })
          : await reactivate.mutateAsync({ clinicId: clinic.id })
      queryClient.setQueryData(getGetClinicQueryKey(clinic.id), updated)
      void queryClient.invalidateQueries({ queryKey: getListClinicsQueryKey() })
      void queryClient.invalidateQueries({ queryKey: getListClinicActivityQueryKey(clinic.id) })
      toast(copy.done)
    } catch (err) {
      toastApiError(toast, err)
    } finally {
      setOpen(false)
    }
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => {
          setOpen(true)
        }}
      >
        {kind === 'deactivate' ? <PowerOff aria-hidden /> : <Power aria-hidden />} {copy.action}
      </button>
      {open && (
        <ConfirmDialog
          title={copy.title}
          body={copy.body}
          action={copy.action}
          danger={kind === 'deactivate'}
          pending={deactivate.isPending || reactivate.isPending}
          onConfirm={() => void confirm()}
          onCancel={() => {
            setOpen(false)
          }}
        />
      )}
    </>
  )
}
